import { useSelector } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

export const useSendChatMessage = (staffId) => {
  const queryClient = useQueryClient();

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const queryKey = ["chatDm", tenantVal, staffId];

  return useMutation({
    mutationFn: async (message) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(`${tenantVal}/chat`, {
        assignToStaffId: Number(staffId),
        message,
        createdByStaffId: Number(tenantVal),
      });

      return response.data;
    },
    onMutate: async (message) => {
      await queryClient.cancelQueries({ queryKey });

      const tempId = `temp-${Date.now()}`;
      const optimisticMessage = {
        id: tempId,
        message,
        sentTime: new Date().toISOString(),
        assignToStaff: { id: Number(staffId), tenant: Number(tenantVal) },
        assignToStaffId: Number(staffId),
        createdByStaff: { id: Number(tenantVal) },
        createdByStaffId: Number(tenantVal),
        readByRecipient: false,
        readAt: null,
        _outgoing: true,
        _status: "sending",
      };

      // The DM cache is now an infinite-query shape (pages[0].messages
      // newest-first), mirroring the channel cache, so optimistic
      // bubbles live on top of page 0.
      queryClient.setQueryData(queryKey, (previous) => {
        if (!previous || !Array.isArray(previous.pages)) {
          return {
            pages: [
              {
                messages: [optimisticMessage],
                page: 0,
                size: 20,
                totalElements: 1,
                totalPages: 1,
                hasNext: false,
                hasPrevious: false,
              },
            ],
            pageParams: [0],
          };
        }
        const pages = previous.pages.map((page, index) =>
          index === 0
            ? { ...page, messages: [optimisticMessage, ...(page.messages || [])] }
            : page
        );

        return { ...previous, pages };
      });

      return { tempId };
    },
    onError: (_err, _message, context) => {
      if (!context?.tempId) return;
      queryClient.setQueryData(queryKey, (previous) => {
        if (!previous || !Array.isArray(previous.pages)) return previous;
        const pages = previous.pages.map((page) => ({
          ...page,
          messages: (page.messages || []).map((msg) =>
            msg.id === context.tempId ? { ...msg, _status: "failed" } : msg
          ),
        }));

        return { ...previous, pages };
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["chats"] });
    },
  });
};
